import React, { useState, useCallback, memo } from 'react';
import {
  Menu,
  MenuItem,
  Box,
  Typography,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import { AccountCircle, Settings, Book, ExitToApp } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthProvider';

interface UserMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  anchorOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

const UserMenu = memo<UserMenuProps>(
  ({
    anchorEl,
    open,
    onClose,
    anchorOrigin = {
      vertical: 'bottom' as const,
      horizontal: 'right' as const
    },
    transformOrigin = {
      vertical: 'top' as const,
      horizontal: 'right' as const
    }
  }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);

    const canAccessCourses = () => {
      return ['ACTIVE', 'SUCCESS', 'ENROLLED'].includes(user?.enrollmentStatus);
    };
    
    const handleMenuClick = useCallback(
      (path: string) => {
        onClose();
        navigate(path);
      },
      [onClose, navigate]
    );

    const handleLogoutClick = useCallback(() => {
      setShowLogoutDialog(true);
    }, []);

    const handleLogoutConfirm = useCallback(async () => {
      try {
        await logout();
      } catch (error) {
        console.error('Logout failed:', error);
      } finally {
        setShowLogoutDialog(false);
        onClose();
      }
    }, [logout, onClose]);

    const handleLogoutCancel = useCallback(() => {
      setShowLogoutDialog(false);
    }, []);

    return (
      <>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={onClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: anchorOrigin.vertical === 'bottom' ? 1.5 : 0,
              ml: anchorOrigin.horizontal === 'left' ? 1 : 0,
              minWidth: 220,
              borderRadius: 2,
              padding: '8px 0'
            }
          }}
        >
          <Box sx={{ px: 2, pt: 1, pb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {user?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>

          <Divider />

          <MenuItem onClick={() => handleMenuClick('/profile')} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>

          {canAccessCourses() && (
            <MenuItem onClick={() => handleMenuClick('/course')} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Book fontSize="small" />
              </ListItemIcon>
              My Courses
            </MenuItem>
          )}

          <Divider />

          <MenuItem
            onClick={handleLogoutClick}
            sx={{
              py: 1.5,
              color: theme.palette.error.main
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.error.main }}>
              <ExitToApp fontSize="small" />
            </ListItemIcon>
            Sign Out
          </MenuItem>
        </Menu>

        <Dialog
          open={showLogoutDialog}
          onClose={handleLogoutCancel}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 320
            }
          }}
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight={600} component="div">
              Confirm Logout
            </Typography>
          </DialogTitle>

          <DialogContent>
            <DialogContentText>
              Are you sure you want to sign out? You'll need to log in again to access your account.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleLogoutCancel} sx={{fontWeight:600}} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleLogoutConfirm} sx={{fontWeight:600}} variant="contained" color="error" autoFocus>
              Sign Out
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

UserMenu.displayName = 'UserMenu';

export default UserMenu;